import {
  RepairSpecAutocompleteCustomFilters,
  RepairSpecAutocompleteCustomFiltersValuesEnablementMap,
} from "@/common/components/Entity/RepairSpec/RepairSpecAutocompleteCustomFiltersInput";
import {
  CreateRepairSpecDto,
  RepairMaterialDto,
  RepairSpecDataDto,
  RepairSpecDto,
  RepairSpecSparePartDto,
  RepairWorkDto,
  UpdateRepairSpecDto,
} from "@/core/api/generated/v1";

import { RepairHelper } from "./repair";

/** Settings for repair spec autocomplete filters. */
export interface RepairSpecAutocompleteSettings {
  /** @deprecated Use `filtersEnablement`. Enabled filters (v1). */
  filters?: {
    partType?: boolean;
    damageType?: boolean;
    make?: boolean;
    model?: boolean;
  };

  /** User custom filters (v2). */
  customFilters?: {
    enablementMap?: Partial<RepairSpecAutocompleteCustomFiltersValuesEnablementMap>;
  };
}

export class RepairSpecHelper {
  /** Checks if there is any currency mismatch between RepairSpec and RepairMaterial/RepairWork. */
  public static isCurrencyMismatch({
    repairSpec,
    repairMaterial,
    repairWork,
    repairSparePart,
  }: {
    repairSpec: RepairSpecDto | CreateRepairSpecDto | UpdateRepairSpecDto | null | undefined;
    repairMaterial?: RepairMaterialDto | null;
    repairWork?: RepairWorkDto | null;
    repairSparePart?: RepairSpecSparePartDto | null;
  }): boolean {
    const currency1 = repairSpec?.currency;
    const currency2 = repairMaterial?.currency || repairWork?.currency || repairSparePart?.currency;

    return (currency1 && currency2 && currency1.code !== currency2.code) ?? false;
  }

  public static isSourceExternal(params: {
    tenantId: Nil<string>;
    repairSpec: Nil<RepairSpecDto | RepairSpecDataDto>;
  }): boolean {
    return (
      !!params.tenantId &&
      !!params.repairSpec?.sourceTenantId &&
      params.tenantId !== params.repairSpec.sourceTenantId
    );
  }

  public static getAutocompleteSettings(): Nil<RepairSpecAutocompleteSettings> {
    const localSettings = RepairHelper.getLocalSettings();
    const settings = localSettings?.repairSpecAutocompleteSettings;
    return settings
      ? {
          ...settings,
          // migrate to v2
          customFilters:
            settings.customFilters ||
            (settings.filters
              ? {
                  enablementMap: {
                    partTypeId: settings.filters.partType,
                    damageTypeId: settings.filters.damageType,
                    makeId: settings.filters.make,
                    modelId: settings.filters.model,
                  },
                }
              : undefined),
        }
      : undefined;
  }

  public static saveAutocompleteSettings(settings: RepairSpecAutocompleteSettings): void {
    const localSettings = RepairHelper.getLocalSettings() || {};
    localSettings.repairSpecAutocompleteSettings = settings;
    RepairHelper.saveLocalSettings(localSettings);
  }

  public static saveAutocompleteCustomFilters(
    filters: Nil<RepairSpecAutocompleteCustomFilters>,
  ): void {
    const localSettings = RepairHelper.getLocalSettings() || {};
    const settings = localSettings?.repairSpecAutocompleteSettings;
    localSettings.repairSpecAutocompleteSettings = {
      ...settings,
      customFilters: {
        ...settings?.customFilters,
        enablementMap: filters?.enablementMapForPersistence,
      },
    };
    RepairHelper.saveLocalSettings(localSettings);
  }
}
